import { useAsync, useDeepCompareEffect, useDeepCompareMemo, useSyncedRef } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Disposable } from "react-relay";
import { fetchQuery, graphql, usePaginationFragment, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import { scmpPlus } from "scmp-sdk";

import { data } from "shared/data";
import { article as articleData } from "shared/data/article";
import { section as sectionData } from "shared/data/section";
import { accountAtom } from "shared/lib/account";

import { useMynewsFeedViewContext } from "scmp-app/components/my-news/feed-view/context";
import type { UserFollowedItem } from "scmp-app/components/my-news/feed-view/type";
import { fetchAiEngineData } from "scmp-app/lib/ai-engine/apis";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import { useMyNews } from "scmp-app/lib/hooks";
import type {
  hooksFeedViewArticlesPaginationQuery,
  hooksFeedViewArticlesPaginationQuery$data,
  hooksFeedViewArticlesPaginationQuery$variables,
} from "scmp-app/queries/__generated__/hooksFeedViewArticlesPaginationQuery.graphql";
import type { hooksFeedViewAuthorFragment$key } from "scmp-app/queries/__generated__/hooksFeedViewAuthorFragment.graphql";
import type { hooksFeedViewAuthorPaginationQuery } from "scmp-app/queries/__generated__/hooksFeedViewAuthorPaginationQuery.graphql";
import type { hooksFeedViewEntityDataAuthorQuery } from "scmp-app/queries/__generated__/hooksFeedViewEntityDataAuthorQuery.graphql";
import type { hooksFeedViewEntityDataSectionQuery } from "scmp-app/queries/__generated__/hooksFeedViewEntityDataSectionQuery.graphql";
import type { hooksFeedViewEntityDataTopicQuery } from "scmp-app/queries/__generated__/hooksFeedViewEntityDataTopicQuery.graphql";
import type { hooksFeedViewSectionFragment$key } from "scmp-app/queries/__generated__/hooksFeedViewSectionFragment.graphql";
import type { hooksFeedViewSectionPaginationQuery } from "scmp-app/queries/__generated__/hooksFeedViewSectionPaginationQuery.graphql";
import type { hooksFeedViewTopicFragment$key } from "scmp-app/queries/__generated__/hooksFeedViewTopicFragment.graphql";
import type { hooksFeedViewTopicPaginationQuery } from "scmp-app/queries/__generated__/hooksFeedViewTopicPaginationQuery.graphql";
const ITEMS_PER_PAGE = 20;

export const useFeedViewArticles = () => {
  const { user } = useAtomValue(accountAtom);
  const { followedIds } = useMyNews();
  const [recommendedArticleIds, setRecommendedArticleIds] = useState<string[]>([]);
  const { selectedItem } = useMynewsFeedViewContext();
  const {
    getMyNewDailyFiveArticleIds: { recommendedArticleIds: myDailyFiveArticleIds },
  } = useMyNews();

  const [{ result }, { execute }] = useAsync(() =>
    fetchAiEngineData<AiEngineDataType.MyNewsHot>({
      authorIds: followedIds.authorIds,
      excludedSections: [
        sectionData.posties.name,
        sectionData.spark.name,
        sectionData.youngPost.name,
        sectionData.yp.name,
      ],
      excludeIds: myDailyFiveArticleIds ?? [],
      limit: 100,
      sectionEntityIds: followedIds.sectionIds,
      topicIds: followedIds.topicIds,
      type: AiEngineDataType.MyNewsHot,
      userUuid: user?.uuid,
    }),
  );

  useEffect(() => {
    const followCount =
      followedIds.authorIds.length + followedIds.sectionIds.length + followedIds.topicIds.length;

    if (followCount < 3 || selectedItem.__typename !== "all") {
      return;
    }

    void execute();
  }, [followedIds, selectedItem, execute]);

  useEffect(() => {
    const data = result?.data;
    if (!data) {
      return;
    }
    const recommendedArticleIds = data.results.map(result => result.id) ?? [];
    setRecommendedArticleIds(recommendedArticleIds);
  }, [result]);

  return { recommendedArticleIds };
};

export const useFeedViewArticlesPagination = (entityIds: string[]) => {
  const environment = useRelayEnvironment();

  const { followedIds } = useMyNews();
  const [contents, setContents] = useState<hooksFeedViewArticlesPaginationQuery$data>();
  const disposablesReference = useRef<Disposable[]>([]);
  const data = useMemo(
    () => contents?.contents?.edges?.map(edge => edge?.node).filter(Boolean) ?? [],
    [contents],
  );

  const [currentPage, setCurrentPage] = useState(0);
  const latestCurrentPage = useSyncedRef(currentPage);

  const [isLoading, setIsLoading] = useState(false);
  const latestIsLoading = useSyncedRef(isLoading);

  const totalPages = Math.ceil(entityIds.length / ITEMS_PER_PAGE);
  const hasNext = useDeepCompareMemo(
    () => entityIds.length > 0 && currentPage < totalPages,
    [entityIds, currentPage, totalPages],
  );
  const latestHasNext = useSyncedRef(hasNext);

  const loadNext = useDeepCompareMemo(
    () => async () => {
      if (latestIsLoading.current || !latestHasNext.current) return;

      try {
        const startIndex = latestCurrentPage.current * ITEMS_PER_PAGE;
        const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, entityIds.length);
        const batchEntityIds = entityIds.slice(startIndex, endIndex);

        const query = graphql`
          query hooksFeedViewArticlesPaginationQuery(
            $entityIds: [String]
            $first: Int!
            $scmpPlusPaywallTypeIds: [String]
          ) {
            contents(
              filter: { entityIds: $entityIds }
              first: $first
              articleExcludeInput: { paywallTypeIds: $scmpPlusPaywallTypeIds }
            ) {
              edges {
                node {
                  ... on Content {
                    ...feedViewArticleContent
                  }
                }
              }
            }
          }
        `;

        const variables: hooksFeedViewArticlesPaginationQuery$variables = {
          entityIds: batchEntityIds,
          first: batchEntityIds.length,
          scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
        };
        const queryRequest = getRequest(query);
        const queryDescriptor = createOperationDescriptor(queryRequest, variables);
        setIsLoading(true);

        const data = await fetchQuery<hooksFeedViewArticlesPaginationQuery>(
          environment,
          query,
          variables,
        ).toPromise();

        if (data) {
          const disposable = environment.retain(queryDescriptor);
          disposablesReference.current.push(disposable);

          setContents(current => {
            if (!current) return data;
            return {
              contents: {
                edges: [...(current.contents?.edges ?? []), ...(data.contents?.edges ?? [])],
              },
            };
          });
        }
      } finally {
        setCurrentPage(page => page + 1);
        setIsLoading(false);
      }
    },
    [entityIds],
  );

  useDeepCompareEffect(() => {
    setIsLoading(false);
    disposablesReference.current.forEach(disposable => disposable.dispose());
    disposablesReference.current = [];
    setContents(undefined);
    setCurrentPage(0);
  }, [entityIds, followedIds]);

  useEffect(
    () => () => {
      disposablesReference.current.forEach(disposable => disposable.dispose());
    },
    [],
  );

  return { data, hasNext, isLoading, loadNext };
};

export const useFeedViewAuthorPagination = (
  fragmentReference: hooksFeedViewAuthorFragment$key | null,
) => {
  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
    hooksFeedViewAuthorPaginationQuery,
    hooksFeedViewAuthorFragment$key
  >(
    graphql`
      fragment hooksFeedViewAuthorFragment on Author
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 20 }
        applicationIds: { type: "[String]", defaultValue: [] }
        excludeEntityIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "hooksFeedViewAuthorPaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          exclude: { entityIds: $excludeEntityIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @connection(key: "hooksFeedViewAuthor__author_contents") {
          edges {
            node {
              ... on Content {
                ...feedViewArticleContent
              }
            }
          }
        }
      }
    `,
    fragmentReference ?? null,
  );

  const articles = useMemo(
    () => data?.contents?.edges?.map(edge => edge.node).filter(Boolean) ?? [],
    [data],
  );

  return { data: articles, hasNext, isLoading: isLoadingNext, loadNext };
};

export const useFeedViewSectionPagination = (
  fragmentReference: hooksFeedViewSectionFragment$key | null,
) => {
  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
    hooksFeedViewSectionPaginationQuery,
    hooksFeedViewSectionFragment$key
  >(
    graphql`
      fragment hooksFeedViewSectionFragment on Section
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 20 }
        applicationIds: { type: "[String]", defaultValue: [] }
        excludeSectionIds: { type: "[String]", defaultValue: [] }
        excludeEntityIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "hooksFeedViewSectionPaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
          exclude: { entityUuids: $excludeSectionIds, entityIds: $excludeEntityIds }
        ) @connection(key: "hooksFeedViewSection__section_contents") {
          edges {
            node {
              ... on Content {
                ...feedViewArticleContent
              }
            }
          }
        }
      }
    `,
    fragmentReference ?? null,
  );

  const articles = useMemo(
    () => data?.contents?.edges?.map(edge => edge.node).filter(Boolean) ?? [],
    [data],
  );

  return { data: articles, hasNext, isLoading: isLoadingNext, loadNext };
};

export const useFeedViewTopicPagination = (
  fragmentReference: hooksFeedViewTopicFragment$key | null,
) => {
  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
    hooksFeedViewTopicPaginationQuery,
    hooksFeedViewTopicFragment$key
  >(
    graphql`
      fragment hooksFeedViewTopicFragment on Topic
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int", defaultValue: 20 }
        applicationIds: { type: "[String]", defaultValue: [] }
        articleTypeIds: { type: "[String]", defaultValue: [] }
        excludeSectionIds: { type: "[String]", defaultValue: [] }
        excludeEntityIds: { type: "[String]", defaultValue: [] }
      )
      @refetchable(queryName: "hooksFeedViewTopicPaginationQuery") {
        contents(
          after: $after
          first: $count
          filter: { applicationIds: $applicationIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
          exclude: {
            articleTypeIds: $articleTypeIds
            sectionIds: $excludeSectionIds
            entityIds: $excludeEntityIds
          }
        ) @connection(key: "hooksFeedViewTopic__topic_contents") {
          edges {
            node {
              ... on Content {
                ...feedViewArticleContent
              }
            }
          }
        }
      }
    `,
    fragmentReference ?? null,
  );

  const articles = useMemo(
    () => data?.contents?.edges?.map(edge => edge.node).filter(Boolean) ?? [],
    [data],
  );

  return { data: articles, hasNext, isLoading: isLoadingNext, loadNext };
};

export const useFeedViewEntityData = (selectedItem: UserFollowedItem) => {
  const environment = useRelayEnvironment();
  const [entityData, setEntityData] = useState<{
    author: hooksFeedViewAuthorFragment$key | null;
    section: hooksFeedViewSectionFragment$key | null;
    topic: hooksFeedViewTopicFragment$key | null;
  }>({ author: null, section: null, topic: null });
  const {
    getMyNewDailyFiveArticleIds: { recommendedArticleIds },
  } = useMyNews();

  useEffect(() => {
    if (selectedItem.__typename === "all") {
      setEntityData({ author: null, section: null, topic: null });
      return;
    }

    let isCancelled = false;

    const fetchEntityData = async () => {
      const { entityId } = selectedItem;
      const variables = {
        applicationIds: [data.application.scmp.entityUuid],
        entityId,
        excludeEntityIds: recommendedArticleIds ?? [],
      };
      const excludeSectionIds = [
        sectionData.posties.entityUuid,
        sectionData.spark.entityUuid,
        sectionData.youngPost.entityUuid,
        sectionData.yp.entityUuid,
      ];

      try {
        switch (selectedItem.__typename) {
          case "Author": {
            const authorData = await fetchQuery<hooksFeedViewEntityDataAuthorQuery>(
              environment,
              graphql`
                query hooksFeedViewEntityDataAuthorQuery(
                  $entityId: String!
                  $applicationIds: [String]
                  $excludeEntityIds: [String]
                ) {
                  author(filter: { entityId: $entityId }) {
                    ...hooksFeedViewAuthorFragment
                      @arguments(
                        applicationIds: $applicationIds
                        excludeEntityIds: $excludeEntityIds
                      )
                  }
                }
              `,
              variables,
            ).toPromise();

            if (!isCancelled) {
              setEntityData({ author: authorData?.author ?? null, section: null, topic: null });
            }
            break;
          }
          case "Section": {
            const sectionData = await fetchQuery<hooksFeedViewEntityDataSectionQuery>(
              environment,
              graphql`
                query hooksFeedViewEntityDataSectionQuery(
                  $entityId: String!
                  $applicationIds: [String]
                  $excludeEntityIds: [String]
                  $excludeSectionIds: [String]
                ) {
                  section(filter: { entityId: $entityId }) {
                    ...hooksFeedViewSectionFragment
                      @arguments(
                        applicationIds: $applicationIds
                        excludeSectionIds: $excludeSectionIds
                        excludeEntityIds: $excludeEntityIds
                      )
                  }
                }
              `,
              {
                ...variables,
                excludeSectionIds,
              },
            ).toPromise();

            if (!isCancelled) {
              setEntityData({ author: null, section: sectionData?.section ?? null, topic: null });
            }
            break;
          }
          case "Topic": {
            const topicData = await fetchQuery<hooksFeedViewEntityDataTopicQuery>(
              environment,
              graphql`
                query hooksFeedViewEntityDataTopicQuery(
                  $entityId: String!
                  $applicationIds: [String]
                  $articleTypeIds: [String]
                  $excludeSectionIds: [String]
                  $excludeEntityIds: [String]
                ) {
                  topic(filter: { entityId: $entityId }) {
                    ...hooksFeedViewTopicFragment
                      @arguments(
                        applicationIds: $applicationIds
                        articleTypeIds: $articleTypeIds
                        excludeSectionIds: $excludeSectionIds
                        excludeEntityIds: $excludeEntityIds
                      )
                  }
                }
              `,
              {
                ...variables,
                articleTypeIds: [
                  articleData.types.opinion.entityUuid,
                  articleData.types.column.entityUuid,
                ],
                excludeSectionIds,
              },
            ).toPromise();

            if (!isCancelled) {
              setEntityData({ author: null, section: null, topic: topicData?.topic ?? null });
            }
            break;
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching entity data:", error);
          setEntityData({ author: null, section: null, topic: null });
        }
      }
    };

    void fetchEntityData();

    return () => {
      isCancelled = true;
    };
  }, [environment, selectedItem, recommendedArticleIds]);

  return entityData;
};
